import type { JSX } from "react";
import { useCallback, useEffect, useRef } from "react";

import { SWReactIcons } from "@similarweb/icons";
import type { ScrollArea as ScrollAreaComponent } from "@similarweb/ui-components";
import { DotsLoader, PixelPlaceholderLoader, ScrollArea, TabList, TabPanel, Tabs } from "@similarweb/ui-components";

import {
    QUICK_SEARCH_TAB_PANEL_SCROLL_MAX_HEIGHT_PX,
    SEARCH_TAB_DICTIONARY,
    TAB_LIST,
} from "app/components/React/QuickSearch/config";
import { quickSearchDataAutomation } from "app/components/React/QuickSearch/dataAutomation";
import { StyledTab, StyledTabText } from "app/components/React/QuickSearch/styles";
import { onHookClick } from "app/components/sidebar3/SideBarItems";
import { translate } from "app/filters/i18nFilter";
import { EPrimarySideBar3StateType } from "app/reducers/_reducers/PrimarySidebar3Reducer";
import { EHooksVersions } from "app/services/SwHook";

import { SearchTabs } from "../types";

type ItemTabsSchema = {
    selectedTab: number;
    listItems: Record<SearchTabs, JSX.Element[]>;
    isKeywordsLocked: boolean;
    isWebsitesLocked?: boolean;
    isLoading?: boolean;
    onSelectTab?: (selectedTab: number) => void;
    /** Max height of the inner scroll region (list under tabs). */
    scrollAreaMaxHeightPx?: number;
    /** Min height of the inner scroll region. When equal to max, the panel has a fixed height. */
    scrollAreaMinHeightPx?: number;
    /** When this value changes, tab list scroll positions reset to the top (e.g. new search query). */
    scrollResetKey?: string;
};

export const ItemTabs = ({
    selectedTab,
    listItems,
    isKeywordsLocked,
    isWebsitesLocked,
    isLoading,
    onSelectTab,
    scrollAreaMaxHeightPx = QUICK_SEARCH_TAB_PANEL_SCROLL_MAX_HEIGHT_PX,
    scrollAreaMinHeightPx = 0,
    scrollResetKey = "",
}: ItemTabsSchema) => {
    const scrollAreaRefs = useRef(new Map<SearchTabs, ScrollAreaComponent>());

    const setScrollAreaRef = useCallback((tab: SearchTabs) => {
        return (scrollArea: ScrollAreaComponent | null) => {
            if (scrollArea) {
                scrollAreaRefs.current.set(tab, scrollArea);
                return;
            }

            scrollAreaRefs.current.delete(tab);
        };
    }, []);

    useEffect(() => {
        scrollAreaRefs.current.forEach((scrollArea) => {
            scrollArea.scrollYTo(0);
        });
    }, [scrollResetKey]);

    const onTabSelect = (index: number) => {
        if (index === selectedTab) {
            return;
        }

        const tab = TAB_LIST[index];

        switch (tab) {
            case SearchTabs.Keyword:
                if (isKeywordsLocked) {
                    onHookClick("keywordResearch", EPrimarySideBar3StateType.KeywordResearch, EHooksVersions.V6);
                    return;
                }
                break;
            case SearchTabs.Website:
                if (isWebsitesLocked) {
                    onHookClick("GetMoreResults", EPrimarySideBar3StateType.WebsiteAnalysis_home, EHooksVersions.V5);
                    return;
                }
                break;
            default:
                break;
        }

        onSelectTab?.(index);
    };

    return (
        <Tabs
            selectedIndex={selectedTab}
            onSelect={onTabSelect}
        >
            <TabList>
                {TAB_LIST.map((tab: SearchTabs) => {
                    const isKeywordTabLocked = tab === SearchTabs.Keyword && isKeywordsLocked;
                    const isWebsiteTabLocked = tab === SearchTabs.Website && isWebsitesLocked;
                    const dataAutomation = quickSearchDataAutomation.popupTab(tab);

                    if (isLoading) {
                        return (
                            <PixelPlaceholderLoader
                                width={200}
                                height={48}
                            />
                        );
                    }
                    if (isKeywordTabLocked || isWebsiteTabLocked) {
                        return (
                            <StyledTab
                                key={tab}
                                data-automation={dataAutomation}
                                className="hookTab"
                            >
                                <StyledTabText>{translate(SEARCH_TAB_DICTIONARY[tab].title)}</StyledTabText>
                                <SWReactIcons
                                    className="hookIcon"
                                    iconName="spark"
                                    size="xs"
                                />
                            </StyledTab>
                        );
                    }

                    return (
                        <StyledTab
                            key={tab}
                            data-automation={dataAutomation}
                        >
                            {translate(SEARCH_TAB_DICTIONARY[tab].title)}
                        </StyledTab>
                    );
                })}
            </TabList>

            {isLoading ? (
                <div>
                    <DotsLoader />
                </div>
            ) : (
                TAB_LIST.map((tab) => (
                    <TabPanel key={tab}>
                        <div data-automation={quickSearchDataAutomation.popupTabContent(tab)}>
                            <ScrollArea
                                ref={setScrollAreaRef(tab)}
                                style={{
                                    maxHeight: scrollAreaMaxHeightPx,
                                    minHeight: scrollAreaMinHeightPx,
                                    transition: "max-height 250ms ease",
                                }}
                                verticalScrollbarStyle={{ borderRadius: 5 }}
                                horizontal={false}
                                smoothScrolling
                                minScrollSize={48}
                            >
                                {listItems[tab]}
                            </ScrollArea>
                        </div>
                    </TabPanel>
                ))
            )}
        </Tabs>
    );
};
